import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const Hero = () => {
  const shouldReduceMotion = useReducedMotion();
  
  const scrollToAPIAccess = () => {
    const element = document.getElementById('api-access');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const scrollToFeatures = () => {
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const gridPattern = "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Ccircle cx='2' cy='2' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";

  // Mobile-optimized animation variants
  const mobileOptimized = {
    initial: { opacity: 0, y: shouldReduceMotion ? 0 : 15 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      duration: shouldReduceMotion ? 0.3 : 0.6, 
      ease: 'easeOut',
      type: 'tween' // Use tween instead of spring for better mobile performance
    }
  };

  return (
    <section
      id="hero"
      className="relative mobile-full-height flex items-center justify-center bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 overflow-hidden pt-20"
    >
      {/* Optimized background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-4 w-48 h-48 xs:top-10 xs:left-10 xs:w-72 xs:h-72 bg-primary-500/8 rounded-full"></div>
        <div className="absolute top-20 right-4 w-56 h-56 xs:top-32 xs:right-10 xs:w-96 xs:h-96 bg-primary-300/8 rounded-full"></div>
        <div className="absolute bottom-10 left-1/4 w-52 h-52 xs:w-80 xs:h-80 bg-primary-400/8 rounded-full"></div>
        
        {/* Simplified grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-30 xs:opacity-60" 
          style={{ backgroundImage: `url("${gridPattern}")` }}
        ></div>
      </div>

      <div className="relative z-10 container-mobile text-center text-white">
        <motion.div
          {...mobileOptimized}
          className="max-w-6xl mx-auto"
        >

          <motion.h1
            id="hero-heading"
            className="text-mobile-hero font-bold mb-8 xs:mb-10 tracking-tight"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.3 : 0.6, 
              delay: shouldReduceMotion ? 0 : 0.1,
              ease: 'easeOut',
              type: 'tween'
            }}
          >
            Infrastructure Lifecycle.{' '}
            <span className="bg-gradient-to-r from-white via-primary-100 to-accent-200 bg-clip-text text-transparent">
              Reinvented.
            </span>
          </motion.h1>

          <motion.p
            className="text-mobile-subtitle font-light max-w-5xl mx-auto mb-10 xs:mb-14 text-primary-100 leading-relaxed"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.3 : 0.6, 
              delay: shouldReduceMotion ? 0 : 0.2,
              ease: 'easeOut',
              type: 'tween'
            }}
          >
            Unifying real-time sensor data, AI analytics, and decision-making tools
            across the full lifecycle of critical infrastructure.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.3 : 0.6, 
              delay: shouldReduceMotion ? 0 : 0.3,
              ease: 'easeOut',
              type: 'tween'
            }}
            className="flex flex-col gap-4 justify-center items-stretch xs:flex-row xs:items-center xs:gap-6 mb-12 xs:mb-16"
          >
            <button
              onClick={scrollToAPIAccess}
              className="group inline-flex items-center justify-center gap-3 bg-white text-primary-800 font-semibold py-4 px-8 xs:px-10 rounded-xl text-lg xs:text-xl hover:bg-primary-50 transition-mobile-smooth shadow-2xl hover:shadow-primary focus:ring-4 focus:ring-white/30 focus:outline-none min-h-touch-md"
            >
              Get API Access
              <svg className="w-5 h-5 xs:w-6 xs:h-6 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            
            <button
              onClick={scrollToFeatures}
              className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-medium py-4 px-8 xs:px-10 rounded-xl text-lg xs:text-xl hover:bg-white/20 hover:border-white/50 transition-mobile-smooth focus:ring-4 focus:ring-white/30 focus:outline-none min-h-touch-md"
            >
              Learn More
              <svg className="w-5 h-5 xs:w-6 xs:h-6 group-hover:translate-y-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          </motion.div>

          {/* Enhanced social proof */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.3 : 0.6, 
              delay: shouldReduceMotion ? 0 : 0.4,
              ease: 'easeOut',
              type: 'tween'
            }}
            className="text-center"
          >
            <p className="text-primary-200 text-base xs:text-lg font-medium mb-6">Trusted by infrastructure teams worldwide</p>
            <div className="flex flex-col xs:flex-row justify-center items-center gap-6 xs:gap-10 opacity-80 mb-12 xs:mb-16">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-white/80 text-sm xs:text-base font-medium">Enterprise Ready</span>
              </div>
              <div className="hidden xs:block w-1 h-1 bg-white/40 rounded-full"></div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-white/80 text-sm xs:text-base font-medium">67+ Sensor Types</span>
              </div>
              <div className="hidden xs:block w-1 h-1 bg-white/40 rounded-full"></div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span className="text-white/80 text-sm xs:text-base font-medium">Real-time AI Analytics</span>
              </div>
            </div>

            {/* Improved scroll indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ 
                duration: shouldReduceMotion ? 0.3 : 0.8, 
                delay: shouldReduceMotion ? 0 : 0.6,
                ease: 'easeOut',
                type: 'tween'
              }}
              className="flex flex-col items-center gap-3 group cursor-pointer touch-target mb-16 xs:mb-20 lg:mb-24"
              onClick={scrollToFeatures}
            >
              <span className="text-sm xs:text-base text-white/70 font-medium group-hover:text-white/90 transition-colors">Scroll to explore</span>
              <div className="animate-bounce-slow">
                <svg
                  className="w-6 h-6 xs:w-8 xs:h-8 text-white/70 group-hover:text-white/90 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero; 